import React from 'react';
import { Table, Tag, Space, Button, Dropdown, Menu, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { 
  EnvironmentOutlined, 
  InboxOutlined, 
  FileTextOutlined, 
  AccountBookOutlined,
  MoreOutlined,
  SettingOutlined
} from '@ant-design/icons';
import type { Project } from '../../../types/project';
import { useAuth } from '../../../hooks/useAuth';
import { usePermissions } from '../../../contexts/PermissionContext';

interface ProjectTableProps {
  data: Project[];
  loading: boolean;
  pagination: any;
  onTableChange: (pagination: any, filters: any, sorter: any) => void;
  onAddress: (record: Project) => void;
  onBasicConfigManage: (record: Project) => void;
  onInventory: (record: Project) => void;
  onOrders: (record: Project) => void;
  onBills: (record: Project) => void;
}

const ProjectTable: React.FC<ProjectTableProps> = ({
  data,
  loading,
  pagination,
  onTableChange,
  onAddress,
  onBasicConfigManage,
  onInventory,
  onOrders,
  onBills
}) => {
  const { user } = useAuth();
  const { isAdmin, isHQStaff, isWarehouseSupervisor, isWarehouseStaff, isMainAccount, isSubAccount, hiddenFields } = usePermissions();
  const isAdminRole = !!isAdmin();
  const isStaffRole = !!(isHQStaff() || isWarehouseSupervisor() || isWarehouseStaff());
  const hidden = hiddenFields('project.Project');
  const hideCustomerByStrategy = Array.isArray(hidden) && hidden.includes('customer__username');
  const hideCustomer = hideCustomerByStrategy || (user?.role === 'customer');
  const isCustomer = user?.role === 'customer';

  // 根据角色获取表格列
  const getTableColumns = (): ColumnsType<Project> => {
    const baseColumns: ColumnsType<Project> = [
      {
        title: '项目编码',
        dataIndex: 'project_code',
        key: 'project_code',
        width: 120,
        render: (text: string) => <span className="font-mono">{text}</span>
      },
      {
        title: '项目名称',
        dataIndex: 'name',
        key: 'name',
        width: 200,
        render: (text: string) => text || '未命名项目'
      },
      {
        title: '项目类型',
        dataIndex: 'project_type',
        key: 'project_type',
        width: 120,
        render: (type: string) => {
          const typeMap: Record<string, { text: string; color: string }> = {
            'OrderFulfillment': { text: '一件代发', color: 'blue' },
            'StockTransfer': { text: '备货中转', color: 'green' },
            'Relabeling': { text: '简单贴换标/清点', color: 'orange' },
            'ReturnProcessing': { text: '复杂退货/退仓处理', color: 'red' },
            'Mixed': { text: '多种业务模式混合', color: 'purple' },
            'Other': { text: '其他', color: 'default' },
          };
          const { text, color } = typeMap[type] || { text: type, color: 'default' };
          return <Tag color={color}>{text}</Tag>;
        },
      }
    ];

    // 若策略要求隐藏客户名称，则不渲染客户列
    if (!hideCustomer && (isAdminRole || isStaffRole)) {
      baseColumns.push({
        title: '客户',
        dataIndex: ['customer', 'username'],
        key: 'customer',
        width: 120,
        render: (text: string) => text || '-'
      });
    }



    // 客户可以看到所属项目组
    if (isCustomer) {
      baseColumns.push({
        title: '所属项目组',
        dataIndex: ['project_group', 'name'],
        key: 'project_group',
        width: 120,
        render: (text: string) => text || '-'
      });
    }

    // 添加项目状态列
    baseColumns.push({
      title: '项目状态',
      dataIndex: 'project_status',
      key: 'project_status',
      width: 120,
      render: (status: string) => {
        const statusKey = (status || '').toString().trim().toLowerCase();
        const statusMap: Record<string, { text: string; color: string }> = {
          'created': { text: '已创建', color: 'blue' },
          // 兼容历史数据：active 按已创建展示
          'active': { text: '已创建', color: 'blue' },
          'terminated': { text: '终止', color: 'red' },
          'exception': { text: '异常', color: 'orange' },
        };
        const { text, color } = statusMap[statusKey] || { text: status, color: 'default' };
        return <Tag color={color}>{text}</Tag>;
      },
    });
    baseColumns.push({
      title: '备注',
      dataIndex: 'description',
      key: 'description',
      width: 220,
      render: (text: string) => {
        const fullText = String(text || '').trim();
        if (!fullText) return '-';
        const shortText = fullText.length > 20 ? `${fullText.slice(0, 20)}...` : fullText;
        return (
          <Tooltip title={fullText}>
            <span>{shortText}</span>
          </Tooltip>
        );
      },
    });

    // 通用列
    baseColumns.push(
      {
        title: '操作',
        key: 'action',
        width: 380,
        fixed: 'right' as const,
        render: (_, record) => renderActions(record)
      }
    );

    return baseColumns;
  };

  // 渲染操作按钮
  const renderActions = (record: Project) => {
    // 主按钮：库存管理、指令管理、账单管理（所有角色均可见）
    const btnInventory = (
      <Tooltip key="inventory" title="查看该项目当前库存">
        <Button type="link" icon={<InboxOutlined />} onClick={() => onInventory(record)}>
          库存管理
        </Button>
      </Tooltip>
    );
    const btnOrders = (
      <Tooltip key="orders" title="查看该项目指令列表">
        <Button type="link" icon={<FileTextOutlined />} onClick={() => onOrders(record)}>
          指令管理
        </Button>
      </Tooltip>
    );
    const btnBills = (isAdminRole || isStaffRole || isMainAccount()) ? (
      <Tooltip key="bills" title="查看该项目账单列表">
        <Button type="link" icon={<AccountBookOutlined />} onClick={() => onBills(record)}>
          账单管理
        </Button>
      </Tooltip>
    ) : null;

    // 更多：专属出入库地址、基础配置管理
    const btnAddress = (
      <Tooltip key="address" title="专属出入库地址">
        <Button type="link" icon={<EnvironmentOutlined />} onClick={() => onAddress(record)}>
          专属出入库地址
        </Button>
      </Tooltip>
    );
    const hasBasicConfig = !!record.has_inventory_config && !!record.inventory_config_id;
    const btnBasicConfigManage = (
      <Tooltip key="basic-config-manage" title={hasBasicConfig ? '进入基础配置详情' : '项目未创建基础配置'}>
        <Button
          type="link"
          icon={<SettingOutlined />}
          disabled={!hasBasicConfig}
          onClick={() => hasBasicConfig && onBasicConfigManage(record)}
        >
          基础配置管理
        </Button>
      </Tooltip>
    );

    const mainActions: React.ReactElement[] = [btnInventory, btnOrders].concat(btnBills ? [btnBills] as React.ReactElement[] : []);
    const moreActions: React.ReactElement[] = [btnAddress, btnBasicConfigManage];

    if (moreActions.length > 0) {
      const menu = (
        <Menu>
          {moreActions.map((action, index) => (
            <Menu.Item key={index}>{action}</Menu.Item>
          ))}
        </Menu>
      );

      return (
        <Space size="middle">
          {mainActions}
          <Dropdown overlay={menu} trigger={['click']}>
            <Button type="link" icon={<MoreOutlined />}>更多</Button>
          </Dropdown>
        </Space>
      );
    }

    return <Space size="middle">{mainActions}</Space>;
  };

  const columns = getTableColumns();

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="id"
      loading={loading}
      pagination={pagination}
      onChange={onTableChange}
      scroll={{ x: 1200 }}
      size="middle"
    />
  );
};

export default ProjectTable;
